import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  BookOpen, CheckCircle, ArrowLeft, Loader2, Crown,
  Map, Clock, ChevronDown, ChevronUp, Sparkles,
  GraduationCap, Briefcase, Rocket, Award, Lock, Target
} from "lucide-react";
import axios from "axios";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkillsPage() {
  const [targetRole, setTargetRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasPass, setHasPass] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [generatingRoadmap, setGeneratingRoadmap] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState(0);
  const navigate = useNavigate();
  const { getHeaders, user } = useAuth();

  useEffect(() => {
    if (!user) { navigate("/auth?return=/skills"); return; }
    fetchData();
  }, [user]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const subRes = await axios.get(`${API}/user/subscription`, { headers: getHeaders() });
      const passStatus = subRes.data.has_season_pass;
      setHasPass(passStatus);
      setTargetRole(subRes.data.target_role || "Software Engineer");

      // Only fetch roadmap data if user has season pass
      if (passStatus) {
        try {
          const roadmapRes = await axios.get(`${API}/skill-path/user-roadmap`, { headers: getHeaders() });
          const data = roadmapRes.data;
          if (data.roadmap) {
            setRoadmap(data.roadmap);
          }
        } catch (e) { /* ok - might be 403 */ }
      }
    } catch (e) { /* ok */ }
    setLoading(false);
  };

  const generatePersonalizedRoadmap = async () => {
    if (!hasPass) {
      toast.error("Full Career OS required for personalized roadmaps");
      navigate("/season-pass");
      return;
    }

    setGeneratingRoadmap(true);
    try {
      const res = await axios.post(`${API}/skill-path/generate`, {
        target_role: targetRole || "Software Engineer",
        experience_level: "Beginner"
      }, { headers: getHeaders() });

      setRoadmap(res.data.roadmap);
      toast.success("Your personalized roadmap is ready!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate roadmap");
    } finally {
      setGeneratingRoadmap(false);
    }
  };

  const phaseIcons = [GraduationCap, Briefcase, Rocket, Award];

  if (loading) return (
    <div className="ap-root min-h-screen pt-24 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--ap-violet)" }} />
    </div>
  );

  // Full Career OS Gate - block access for non-pass users
  if (!hasPass) {
    return (
      <div className="ap-root min-h-screen pt-24 pb-16" data-testid="skills-page-locked">
        <div className="ap-section">
          <div className="max-w-2xl mx-auto text-center space-y-8 py-8">
            <div className="ap-rise">
              <div
                className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto"
                style={{ background: "var(--ap-violet-soft)" }}
              >
                <Lock className="w-9 h-9" style={{ color: "var(--ap-violet)" }} />
              </div>
            </div>
            <div className="ap-rise ap-rise-1 space-y-4">
              <span className="ap-eyebrow">Full Career OS</span>
              <h1 className="ap-display text-4xl md:text-5xl font-extrabold" style={{ color: "var(--ap-ink)" }}>
                Your personal <span className="ap-grad-text">Skill Path</span>
              </h1>
              <p className="text-base md:text-lg max-w-md mx-auto" style={{ color: "var(--ap-muted)" }}>
                Get AI-powered personalized learning roadmaps and career guidance tailored to your target role.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto ap-rise ap-rise-2">
              <div className="ap-card ap-pop p-6 text-center">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center mx-auto mb-3"
                  style={{ background: "var(--ap-violet-soft)" }}
                >
                  <Map className="w-5 h-5" style={{ color: "var(--ap-violet)" }} />
                </div>
                <p className="text-sm font-semibold" style={{ color: "var(--ap-ink)" }}>AI Roadmaps</p>
              </div>
              <div className="ap-card ap-pop p-6 text-center">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center mx-auto mb-3"
                  style={{ background: "rgba(147,51,234,0.08)" }}
                >
                  <BookOpen className="w-5 h-5" style={{ color: "#9333ea" }} />
                </div>
                <p className="text-sm font-semibold" style={{ color: "var(--ap-ink)" }}>Skill Guidance</p>
              </div>
              <div className="ap-card ap-pop p-6 text-center">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center mx-auto mb-3"
                  style={{ background: "rgba(255,107,53,0.10)" }}
                >
                  <Target className="w-5 h-5" style={{ color: "var(--ap-orange)" }} />
                </div>
                <p className="text-sm font-semibold" style={{ color: "var(--ap-ink)" }}>Career Tips</p>
              </div>
            </div>

            <div className="ap-rise ap-rise-3 max-w-md mx-auto">
              <div
                className="ap-mesh rounded-3xl p-8 text-center"
                style={{ background: "#fff", border: "1px solid var(--ap-line)", boxShadow: "var(--ap-shadow)" }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: "var(--ap-fire)", boxShadow: "var(--ap-glow)" }}
                >
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <h3 className="ap-display text-xl font-bold mb-2" style={{ color: "var(--ap-ink)" }}>
                  Full Career OS Required
                </h3>
                <p className="text-sm mb-6" style={{ color: "var(--ap-muted)" }}>
                  Unlock Skill Path and all premium features with Full Career OS.
                </p>
                <button
                  onClick={() => navigate("/season-pass")}
                  className="ap-btn ap-btn-fire ap-btn-lg"
                  data-testid="skills-upgrade-btn"
                >
                  <Crown className="w-4 h-4" /> GET FULL CAREER OS
                </button>
              </div>
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className="bg-transparent border-0 cursor-pointer text-sm flex items-center gap-1.5 mx-auto ap-rise ap-rise-4"
              style={{ color: "var(--ap-muted)" }}
            >
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-root min-h-screen pt-24 pb-16" data-testid="skills-page">
      <div className="ap-section">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Header with target role context */}
          <div className="flex items-center justify-between gap-4 flex-wrap ap-rise">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="ap-btn ap-btn-ghost ap-btn-md"
                style={{ padding: "10px" }}
                data-testid="skills-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="ap-display text-3xl font-extrabold flex items-center gap-3" style={{ color: "var(--ap-ink)" }}>
                  <Map className="w-7 h-7" style={{ color: "var(--ap-violet)" }} /> Skill Path
                </h1>
                <p className="text-sm mt-1" style={{ color: "var(--ap-muted)" }}>
                  Personalized roadmap for: <span className="font-semibold" style={{ color: "var(--ap-violet)" }}>{targetRole || "Not set"}</span>
                </p>
              </div>
            </div>
            {hasPass && (
              <span className="ap-chip" style={{ color: "var(--ap-violet)" }}>
                <Crown className="w-3.5 h-3.5" /> Full Career OS
              </span>
            )}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 ap-rise ap-rise-1">
            <div className="ap-card ap-pop p-5 text-center">
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--ap-muted)" }}>Target Role</p>
              <p className="font-semibold text-sm truncate" style={{ color: "var(--ap-ink)" }}>{targetRole || "Not set"}</p>
            </div>
            <div className="ap-card ap-pop p-5 text-center">
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--ap-muted)" }}>Phases</p>
              <p className="ap-display text-2xl font-extrabold" style={{ color: "var(--ap-violet)" }}>{roadmap?.phases?.length || 4}</p>
            </div>
            <div className="ap-card ap-pop p-5 text-center">
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--ap-muted)" }}>Est. Time</p>
              <p className="ap-display text-2xl font-extrabold" style={{ color: "#9333ea" }}>{roadmap?.total_duration || "6-9 mo"}</p>
            </div>
          </div>

          {/* ROADMAP Content */}
          <div className="space-y-6">
            {/* Generate Button (if no roadmap) */}
            {!roadmap && (
              <div className="ap-mesh ap-card rounded-3xl p-10 text-center ap-rise ap-rise-2">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: "var(--ap-violet-soft)" }}
                >
                  <Sparkles className="w-8 h-8" style={{ color: "var(--ap-violet)" }} />
                </div>
                <h3 className="ap-display text-2xl font-bold mb-3" style={{ color: "var(--ap-ink)" }}>
                  Generate Your Personalized Roadmap
                </h3>
                <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: "var(--ap-muted)" }}>
                  Get an AI-powered learning path tailored to your target role: <strong style={{ color: "var(--ap-ink)" }}>{targetRole}</strong>
                </p>
                <button
                  onClick={generatePersonalizedRoadmap}
                  disabled={generatingRoadmap}
                  className="ap-btn ap-btn-fire ap-btn-lg mx-auto"
                  style={generatingRoadmap ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                >
                  {generatingRoadmap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {generatingRoadmap ? "Generating..." : "Generate Roadmap"}
                </button>
              </div>
            )}

            {/* Roadmap Overview */}
            {roadmap && (
              <>
                <div className="ap-card p-6 ap-rise ap-rise-2">
                  <h3 className="ap-display font-bold text-lg mb-4" style={{ color: "var(--ap-ink)" }}>
                    {roadmap.overview || "Your personalized learning journey"}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <span className="ap-chip"><Clock className="w-3.5 h-3.5" style={{ color: "var(--ap-violet)" }} /> {roadmap.total_duration}</span>
                    <span className="ap-chip"><Target className="w-3.5 h-3.5" style={{ color: "var(--ap-violet)" }} /> {roadmap.phases?.length || 0} Phases</span>
                    <span className="ap-chip"><BookOpen className="w-3.5 h-3.5" style={{ color: "var(--ap-violet)" }} /> {roadmap.phases?.reduce((sum, p) => sum + (p.courses?.length || 0), 0) || 0} Courses</span>
                  </div>
                </div>

                {/* Phases timeline */}
                <div className="space-y-4">
                  {roadmap.phases?.map((phase, i) => {
                    const PhaseIcon = phaseIcons[i % phaseIcons.length];
                    const isExpanded = expandedPhase === i;

                    return (
                      <Card
                        key={i}
                        className="ap-card transition-all"
                        style={{ borderColor: isExpanded ? "var(--ap-violet)" : "var(--ap-line)", boxShadow: isExpanded ? "var(--ap-shadow)" : "var(--ap-shadow-sm)" }}
                      >
                        <CardHeader
                          className="cursor-pointer pb-0"
                          onClick={() => setExpandedPhase(isExpanded ? -1 : i)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div
                                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                                style={{ background: isExpanded ? "var(--ap-violet)" : "var(--ap-violet-soft)" }}
                              >
                                <PhaseIcon className="w-6 h-6" style={{ color: isExpanded ? "#fff" : "var(--ap-violet)" }} />
                              </div>
                              <div>
                                <h4 className="font-bold text-base" style={{ color: "var(--ap-ink)" }}>
                                  Phase {phase.phase_number}: {phase.phase_name}
                                </h4>
                                <p className="text-sm mt-1" style={{ color: "var(--ap-muted)" }}>{phase.duration} • {phase.courses?.length || 0} courses</p>
                              </div>
                            </div>
                            {isExpanded
                              ? <ChevronUp className="w-5 h-5 shrink-0" style={{ color: "var(--ap-muted)" }} />
                              : <ChevronDown className="w-5 h-5 shrink-0" style={{ color: "var(--ap-muted)" }} />}
                          </div>
                        </CardHeader>

                        {isExpanded && (
                          <CardContent className="pt-6">
                            <p className="text-sm mb-5" style={{ color: "var(--ap-ink-2)" }}>{phase.description}</p>

                            {/* Skills to Learn */}
                            {phase.skills_to_learn?.length > 0 && (
                              <div className="mb-5">
                                <p className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--ap-muted)" }}>Skills You'll Learn</p>
                                <div className="flex flex-wrap gap-2">
                                  {phase.skills_to_learn.map((skill, j) => (
                                    <Badge
                                      key={j}
                                      className="border-0 text-xs px-3 py-1"
                                      style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}
                                    >{skill}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Milestone */}
                            {phase.milestone && (
                              <div className="p-4 rounded-2xl" style={{ background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.18)" }}>
                                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--ap-green)" }}>🎯 Milestone</p>
                                <p className="text-sm" style={{ color: "#15803d" }}>{phase.milestone}</p>
                              </div>
                            )}

                            {/* Projects */}
                            {phase.projects?.length > 0 && (
                              <div className="mt-4 p-4 rounded-2xl" style={{ background: "rgba(147,51,234,0.05)", border: "1px solid rgba(147,51,234,0.18)" }}>
                                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "#9333ea" }}>🔨 Practice Projects</p>
                                <ul className="text-sm list-disc list-inside space-y-1" style={{ color: "#7e22ce" }}>
                                  {phase.projects.map((proj, j) => <li key={j}>{proj}</li>)}
                                </ul>
                              </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>

                {/* Career Tips */}
                {roadmap.career_tips?.length > 0 && (
                  <div
                    className="rounded-3xl p-6 ap-rise"
                    style={{ background: "linear-gradient(120deg, #fffbeb, #fff7ed)", border: "1px solid #fde68a" }}
                  >
                    <h4 className="ap-display font-bold mb-4 flex items-center gap-2" style={{ color: "#92400e" }}>
                      <Sparkles className="w-5 h-5" /> Career Tips
                    </h4>
                    <ul className="space-y-3">
                      {roadmap.career_tips.map((tip, i) => (
                        <li key={i} className="text-sm flex items-start gap-3" style={{ color: "#b45309" }}>
                          <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#f59e0b" }} />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
